import { useMemo } from 'react';
import type { PayRunCycleLogWithReferences } from '@core/payroll/payroll.type';
import PayrollStatusPill from '@/modules/payroll/components/shared/PayrollStatusPill';
import { sortCyclesNewestFirst } from '@/modules/payroll/utils/payRunCycle.utils';
import {
  formatCents,
  formatDateRangeLabel,
  formatDateTimeLabel,
} from '@/modules/payroll/utils/payrollFormat.utils';

type CycleLogListProps = {
  cycleLogs: PayRunCycleLogWithReferences[];
  selectedCycleId: string | null;
  onSelect: (cycleId: string) => void;
  isLoading: boolean;
};

const CycleLogList = ({
  cycleLogs,
  selectedCycleId,
  onSelect,
  isLoading,
}: CycleLogListProps) => {
  const sortedCycles = useMemo(
    () => sortCyclesNewestFirst(cycleLogs),
    [cycleLogs],
  );

  if (isLoading && cycleLogs.length === 0) {
    return <p className="py-6 text-sm text-gray-500">Fetching cycles…</p>;
  }

  if (cycleLogs.length === 0) {
    return (
      <p className="py-6 text-sm text-gray-500">
        No pay run cycles found for this period.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {sortedCycles.map((log) => {
        const isSelected = log.id === selectedCycleId;
        return (
          <li key={log.id}>
            <button
              type="button"
              onClick={() => onSelect(log.id)}
              className={`flex w-full flex-col gap-1 rounded-lg border p-3 text-left transition-colors ${
                isSelected
                  ? 'border-[#03406A] bg-[#03406A]/5'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  {formatDateRangeLabel(
                    log.payRunDateFromISO,
                    log.payRunDateToISO,
                  )}
                </span>
                <PayrollStatusPill status={log.status} />
              </div>
              <span className="text-xs text-gray-600">
                {log.payRunCount} pay runs · Payable{' '}
                {formatCents(log.totalPayableAmountCents)} · Billable{' '}
                {formatCents(log.totalClientBillableAmountCents)}
              </span>
              <span className="text-xs text-gray-500">
                Run at {formatDateTimeLabel(log.createdAt)}
              </span>
              {log.errorSummary.length > 0 && (
                <span className="text-xs text-red-600">
                  {log.errorSummary.length} errors
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default CycleLogList;
